import { handleException } from 'clients/exception-handling/handle-exception'
import {
  OPERATION_STATUS,
  type OperationOutcome,
  type OperationPolicy,
} from 'clients/exception-handling/interface'
import { logger, type LoggerClient } from 'clients/logger'
import { isCancellation } from 'clients/logger/is-cancellation'
import { normalizeException } from 'clients/logger/normalize-exception'

/** Executes a user operation without turning rejection into apparent success. */
export const runOperation = async <T>(
  task: () => T | Promise<T>,
  policy: OperationPolicy<T>,
  reporter: LoggerClient = logger,
): Promise<OperationOutcome<T>> => {
  let outcome: OperationOutcome<T>
  let terminalFailureReported = false
  try {
    outcome = { status: OPERATION_STATUS.SUCCEEDED, value: await task() }
  } catch (cause) {
    try {
      if (isCancellation(cause)) {
        outcome = { status: OPERATION_STATUS.CANCELLED, cause }
      } else if (policy.isExpectedError?.(cause)) {
        outcome = { status: OPERATION_STATUS.EXPECTED, cause }
      } else if (policy.recover) {
        const value = await policy.recover.run(cause)
        outcome = { status: OPERATION_STATUS.RECOVERED, value, cause }
      } else {
        outcome = { status: OPERATION_STATUS.FAILED, cause }
      }
    } catch (recoveryCause) {
      outcome = {
        status: isCancellation(recoveryCause)
          ? OPERATION_STATUS.CANCELLED
          : OPERATION_STATUS.FAILED,
        cause: recoveryCause,
        originalCause: cause,
      }
    }
  }

  try {
    await policy.cleanup?.()
  } catch (cleanupCause) {
    if (
      outcome.status === OPERATION_STATUS.SUCCEEDED ||
      outcome.status === OPERATION_STATUS.RECOVERED ||
      outcome.status === OPERATION_STATUS.FAILED ||
      isCancellation(cleanupCause)
    ) {
      outcome = { ...outcome, cleanupCause }
    } else {
      terminalFailureReported = true
      outcome = {
        status: OPERATION_STATUS.FAILED,
        cause: cleanupCause,
        originalCause: outcome.cause,
      }
    }
    handleException(
      cleanupCause,
      {
        operation: `${policy.operation}.cleanup`,
        message: 'Operation cleanup failed.',
        context: policy.context,
      },
      reporter,
    )
  }

  if (outcome.status === OPERATION_STATUS.FAILED && !terminalFailureReported) {
    const reportCause =
      'originalCause' in outcome
        ? new AggregateError(
            [
              normalizeException(outcome.originalCause),
              normalizeException(outcome.cause),
            ],
            'Operation and recovery failed.',
          )
        : outcome.cause
    handleException(reportCause, policy, reporter)
  } else if (outcome.status === OPERATION_STATUS.RECOVERED && policy.recover) {
    handleException(
      outcome.cause,
      {
        ...policy,
        disposition: 'recovered',
        reason: policy.recover.reason,
      },
      reporter,
    )
  }
  return outcome
}
